import type { FocusEventHandler, ReactNode } from "react";
import { memo, useEffect, useMemo } from "react";

import type { AutocompleteProps, AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import Autocomplete from "@mui/material/Autocomplete";
import type { SxProps, Theme } from "@mui/material/styles";
import type { UseAutocompleteProps } from "@mui/material/useAutocomplete";
import { useTranslation } from "react-i18next";

import type { DropdownOption, FlexibleOption } from "./utils";
import {
  filterOptions,
  flexibleDropdownOptionsToDropdownOptions,
  getOptionLabel,
  isOptionEqualToValue,
  renderOptionSolo,
} from "./utils";

type BaseAutoCompleteSingleSelectProps = {
  id: string;
  onBlur?: FocusEventHandler<HTMLDivElement> | undefined;
  value: DropdownOption | number | string | null;
  options: UseAutocompleteProps<FlexibleOption, false, boolean, false>["options"];
  renderInput: (params: AutocompleteRenderInputParams) => ReactNode;
  getOptionDisabled?: UseAutocompleteProps<DropdownOption, false, boolean, false>["getOptionDisabled"];
  disableClearable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  sortKey?: keyof DropdownOption | null;
  sx?: SxProps<Theme>;
  autoSelect?: boolean;
};

export type DefaultAutoCompleteSingleSelectProps = BaseAutoCompleteSingleSelectProps & {
  freeSolo?: false;
  onChange: NonNullable<UseAutocompleteProps<DropdownOption, false, false, false>["onChange"]>;
  groupBy?: AutocompleteProps<DropdownOption, false, false, false>["groupBy"];
  renderOption?: AutocompleteProps<DropdownOption, false, boolean, false>["renderOption"];
  onInputChange?: UseAutocompleteProps<DropdownOption, false, boolean, false>["onInputChange"];
};

export type FreeSoloAutoCompleteSingleSelectProps = BaseAutoCompleteSingleSelectProps & {
  freeSolo: true;
  onChange: NonNullable<UseAutocompleteProps<DropdownOption, false, false, true>["onChange"]>;
  groupBy?: AutocompleteProps<DropdownOption, false, false, true>["groupBy"];
  renderOption?: AutocompleteProps<DropdownOption, false, boolean, true>["renderOption"];
  onInputChange?: UseAutocompleteProps<DropdownOption, false, boolean, true>["onInputChange"];
};

type AutoCompleteSingleSelectProps =
  | DefaultAutoCompleteSingleSelectProps
  | FreeSoloAutoCompleteSingleSelectProps;

const AutoCompleteSingleSelect = (props: AutoCompleteSingleSelectProps) => {
  const {
    id,
    value,
    options,
    onBlur,
    renderInput,
    disableClearable = false,
    disabled = false,
    getOptionDisabled,
    loading = false,
    groupBy,
    fullWidth = false,
    loadingText,
    renderOption,
    onInputChange,
    onChange,
    sortKey = "displayName",
    freeSolo,
    sx,
    autoSelect = false,
  } = props;

  const { t } = useTranslation(["common"]);

  const optionsClean = useMemo(() => {
    return flexibleDropdownOptionsToDropdownOptions(options, sortKey);
  }, [options, sortKey]);

  const loadingTextValue = loadingText ?? t("common:loading_ellipses");

  const valueNormalized = useMemo(() => {
    if (typeof value === "number") {
      return optionsClean.find((option) => option.id === String(value)) ?? null;
    }
    if (typeof value === "string") {
      return optionsClean.find((option) => option.id === value) ?? (freeSolo ? value : null);
    }
    return value;
  }, [freeSolo, optionsClean, value]);

  useEffect(() => {
    if (!autoSelect || disabled || freeSolo) return;
    if (valueNormalized !== null) return;
    if (optionsClean.length !== 1) return;
    if ((getOptionDisabled ?? ((option) => Boolean(option.disabled)))(optionsClean[0])) return;

    onChange({} as never, optionsClean[0], "selectOption");
  }, [autoSelect, disabled, freeSolo, getOptionDisabled, onChange, optionsClean, valueNormalized]);

  const commonProps = {
    "data-component": "Autocomplete",
    id,
    size: "small" as const,
    fullWidth,
    multiple: false as const,
    disableClearable,
    blurOnSelect: true,
    disableCloseOnSelect: true,
    onBlur,
    autoHighlight: true,
    groupBy,
    options: optionsClean,
    filterOptions,
    onInputChange,
    isOptionEqualToValue,
    getOptionLabel,
    getOptionDisabled: getOptionDisabled ?? ((option: DropdownOption) => Boolean(option.disabled)),
    renderInput,
    disabled,
    loading,
    loadingText: loadingTextValue,
    sx,
  };

  let freeSoloSpecificProps = {};
  if (freeSolo) {
    freeSoloSpecificProps = {
      freeSolo,
      value: valueNormalized,
      onChange,
      renderOption: renderOption ?? renderOptionSolo,
    };
    freeSoloSpecificProps = { freeSolo, value: valueNormalized, onChange };
  } else {
    freeSoloSpecificProps = {
      freeSolo: false,
      value: typeof valueNormalized === "string" ? null : valueNormalized,
      onChange,
      renderOption: renderOption ?? renderOptionSolo,
    };
  }

  return <Autocomplete {...commonProps} {...freeSoloSpecificProps} />;
};

export default memo(AutoCompleteSingleSelect);
